/**
 * Экран «Запросы»: запрос → позиции (перенос листа «Запросы» из Engine.gs 3.13.2).
 * Излишек по OEM показывается в самом новом запросе, где этот OEM есть.
 */
import { articleMetrics, requestKey, lineKey } from '../engine/engine.js';
import { normalizeArticle, round2 } from '../engine/values.js';
import { formatDay } from '../format.js';
/** Для каждого OEM — запрос, в строке которого показывается излишек. */
function surplusOwners(eng) {
    const owners = {};
    for (const req of eng.requests) {
        const key = requestKey(req.no);
        for (const it of req.items) {
            const a = normalizeArticle(it.art);
            const active = !!eng.linesByKey[lineKey(key, a)];
            const cur = owners[a];
            if (active || !cur || !cur.active)
                owners[a] = { key, active };
        }
    }
    const out = {};
    for (const [a, v] of Object.entries(owners))
        out[a] = v.key;
    return out;
}
export function buildRequestsView(eng) {
    const owners = surplusOwners(eng);
    // Новые запросы сверху
    return eng.requests
        .slice()
        .reverse()
        .map((req) => {
        const key = requestKey(req.no);
        const s = eng.reqSummary[key];
        let cancelled = 0;
        let factoryAmount = 0;
        let hasFactoryAmount = false;
        let margin = 0;
        let marginLines = 0;
        let boughtAmount = 0;
        let hasBoughtAmount = false;
        let surplus = 0;
        const items = req.items.map((it) => {
            const art = normalizeArticle(it.art);
            const ag = eng.arts[art];
            const line = eng.linesByKey[lineKey(key, art)];
            const m = ag ? articleMetrics(ag) : null;
            const factoryPrice = ag?.factoryPrice ?? null;
            const free = owners[art] === key && m ? m.free : 0;
            surplus += free;
            if (!line) {
                cancelled++;
                return {
                    art,
                    suggestion: ag?.suggestion ?? '',
                    name: it.name,
                    qty: 0,
                    price: it.price,
                    amount: null,
                    factoryPrice,
                    factoryAmount: null,
                    margin: null,
                    bought: 0,
                    boughtAmount: null,
                    toBuy: 0,
                    surplus: free,
                    status: 'Отменено',
                    note: it.note,
                    cancelled: true,
                    packable: 0,
                    stock: m?.stockLeft ?? null,
                    stockDate: ag?.stockDate ?? null
                };
            }
            const amount = it.price !== null ? round2(line.demand * it.price) : null;
            const famount = factoryPrice !== null ? round2(line.demand * factoryPrice) : null;
            if (famount !== null) {
                factoryAmount += famount;
                hasFactoryAmount = true;
            }
            let itemMargin = null;
            if (amount !== null && famount !== null) {
                itemMargin = round2(amount - famount);
                margin += itemMargin;
                marginLines++;
            }
            if (line.boughtAmount !== null) {
                boughtAmount += line.boughtAmount;
                hasBoughtAmount = true;
            }
            return {
                art,
                suggestion: ag?.suggestion ?? '',
                name: it.name,
                qty: line.demand,
                price: it.price,
                amount,
                factoryPrice,
                factoryAmount: famount,
                margin: itemMargin,
                bought: line.bought,
                boughtAmount: line.boughtAmount,
                toBuy: line.toBuy,
                surplus: free,
                status: line.status,
                note: it.note,
                cancelled: false,
                packable: m?.packable ?? 0,
                stock: m?.stockLeft ?? null,
                stockDate: ag?.stockDate ?? null
            };
        });
        return {
            no: req.no,
            created: req.created,
            deadline: req.deadline,
            author: req.createdBy,
            // Выполненный запрос: в начале комментария — когда его закрыли
            comment: [s.status === 'Выполнен' && req.doneAt ? `Дата выполнения: ${formatDay(req.doneAt)}` : '', req.comment]
                .filter(Boolean)
                .join(' · '),
            status: s.status,
            positions: s.positions,
            cancelled,
            qty: s.qty,
            bought: s.bought,
            done: s.done,
            amount: s.amount,
            factoryAmount: hasFactoryAmount ? round2(factoryAmount) : null,
            margin: marginLines ? round2(margin) : null,
            boughtAmount: hasBoughtAmount ? round2(boughtAmount) : null,
            surplus,
            items
        };
    });
}
/** Столбцы выгрузки: пользователь выбирает, какие нужны (выбор запоминается). «№ запроса / OEM» — всегда. */
export const REQUEST_EXPORT_COLUMNS = [
    { key: 'tree', title: '№ запроса / OEM', width: 26, format: 'text', fixed: true },
    { key: 'suggest', title: 'OEM от закупки', width: 20, format: 'text' },
    { key: 'name', title: 'Наименование', width: 38, format: 'text' },
    { key: 'qty', title: 'Требуемое кол-во, шт', width: 13, format: 'int' },
    { key: 'price', title: 'Цена для клиента, CNY', width: 15, format: 'money' },
    { key: 'amount', title: 'Сумма для клиента, CNY', width: 16, format: 'money' },
    { key: 'fprice', title: 'Цена фабрики, CNY', width: 15, format: 'money' },
    { key: 'famount', title: 'Сумма фабрики, CNY', width: 16, format: 'money' },
    { key: 'margin', title: 'Маржа, CNY', width: 14, format: 'money' },
    { key: 'bought', title: 'Уже закуплено, шт', width: 13, format: 'int' },
    { key: 'boughtAmount', title: 'Закуплено на сумму для клиента, CNY', width: 18, format: 'money' },
    { key: 'tobuy', title: 'Нужно закупить, шт', width: 14, format: 'int' },
    { key: 'stock', title: 'Наличие у фабрики, шт', width: 14, format: 'int' },
    { key: 'stockDate', title: 'Дата наличия', width: 13, format: 'text' },
    { key: 'surplus', title: 'Излишек, шт', width: 12, format: 'int' },
    { key: 'status', title: 'Статус', width: 20, format: 'text' },
    { key: 'author', title: 'Автор', width: 20, format: 'text' },
    { key: 'note', title: 'Комментарий / примечание', width: 34, format: 'text' }
];
//# sourceMappingURL=requests.js.map